import { useQuery } from '@tanstack/react-query'

import { getListComment } from '@/features/view-tfl-uploaded/service'
import { convertSortToApiFormat, mergeTablePaginationParams } from '@/utils'
import {
  DEFAULT_PAGE,
  DEFAULT_STALE_TIME_MS,
  MIN_SEARCH_LENGTH,
  SortDirection,
  TABLE_PAGE_SIZE,
} from '@/utils/constants'

import { COMMENT_LIST_QUERY_KEY } from './constants'
import { CommentFilters } from './types'

// Map frontend column IDs to backend field names
const SORT_COLUMN_MAPPING: Record<string, string> = {
  deliverable_set: 'deliverable_set_name',
  thread_status: 'status',
}

export const useCommentList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: {
    column: string
    direction: SortDirection
  }
  filters?: CommentFilters
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    filters,
  } = params
  const query = search.trim().length >= MIN_SEARCH_LENGTH ? search.trim() : ''

  // Map column ID to API field name
  const mappedSort = sort?.column
    ? {
        column: SORT_COLUMN_MAPPING[sort.column] || sort.column,
        direction: sort.direction,
      }
    : { column: null, direction: null }

  const sortBy = convertSortToApiFormat(mappedSort, '-created_at')
  const filterBy: Record<string, string[]> = {}

  if (filters?.deliverableSetId) {
    filterBy.deliverable_set_id = [filters.deliverableSetId]
  }
  if (filters?.projectIds) {
    filterBy.project_id = filters.projectIds
  }
  if (filters?.tflFileId) {
    filterBy.document_id = [filters.tflFileId]
  }
  if (filters?.status) {
    filterBy.status = [filters.status]
  }
  if (filters?.reviewStatus) {
    // Map review status to actionable_type since review status is calculated from actionable_type in FE
    if (filters.reviewStatus === 'not_reviewed') {
      filterBy.actionable_type = ['none']
    } else if (filters.reviewStatus === 'reviewed') {
      // If action filter is set with reviewed status, use action; otherwise show all reviewed
      filterBy.actionable_type = filters.action
        ? [filters.action]
        : ['actionable', 'not_actionable']
    }
  } else if (filters?.action) {
    filterBy.actionable_type = [filters.action]
  }

  return useQuery({
    queryKey: COMMENT_LIST_QUERY_KEY([
      query,
      page,
      rowsPerPage,
      sortBy,
      JSON.stringify(filterBy),
    ]),
    queryFn: () => {
      const requestBody = mergeTablePaginationParams({
        q: query,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
        filter_by: filterBy,
      })
      return getListComment(requestBody)
    },
    select: (response) => ({
      data: response.data,
      pagination: response.pagination,
    }),
    refetchOnReconnect: 'always',
    refetchOnWindowFocus: 'always',
    staleTime: DEFAULT_STALE_TIME_MS,
  })
}
